/**
 * Shared rendering of a `MigrationResult`. Both the project-settings
 * "schema re-upload" preview and the schema-editor save dialog show
 * migration impact; they previously had separate components with subtle
 * differences in copy and grouping. This is the single source of truth.
 */

import type { MigrationResult } from '@bliksys/schema-engine';

import './MigrationImpact.css';

interface Props {
  migration: MigrationResult;
  /** When true, the changes section starts open; otherwise collapsed. */
  defaultOpen?: boolean;
  /** Pass-through wrapper class for layout-specific tweaks. */
  className?: string;
}

export function MigrationImpact({ migration, defaultOpen = false, className }: Props): JSX.Element {
  const { summary, changes } = migration;
  const hasChanges = changes.length > 0;

  return (
    <div className={`migration-impact${className ? ` ${className}` : ''}`}>
      <div className="migration-impact-summary" aria-live="polite">
        {!hasChanges && <Pill kind="clean">No changes</Pill>}
        {summary.dropped > 0 && (
          <Pill kind="drop">
            <strong>{summary.dropped}</strong> dropped
          </Pill>
        )}
        {summary.defaulted > 0 && (
          <Pill kind="default">
            <strong>{summary.defaulted}</strong> defaulted
          </Pill>
        )}
      </div>
      {hasChanges && (
        <details className="migration-impact-details" open={defaultOpen}>
          <summary>
            Show {changes.length} change{changes.length === 1 ? '' : 's'}
          </summary>
          <p className="migration-impact-hint">Paths point to fields in the existing content.</p>
          <ul className="migration-impact-changes">
            {changes.slice(0, 50).map((c, i) => (
              <li
                key={i}
                className={`migration-impact-change migration-impact-change-${kindClass(c.kind)}`}
              >
                <code className="migration-impact-path">{c.path || '/'}</code>
                <span className="migration-impact-message">{c.message}</span>
              </li>
            ))}
            {changes.length > 50 && (
              <li className="migration-impact-overflow">…and {changes.length - 50} more</li>
            )}
          </ul>
        </details>
      )}
    </div>
  );
}

function Pill({
  kind,
  children,
}: {
  kind: 'clean' | 'drop' | 'default';
  children: React.ReactNode;
}): JSX.Element {
  return <span className={`migration-impact-pill migration-impact-pill-${kind}`}>{children}</span>;
}

function kindClass(kind: MigrationResult['changes'][number]['kind']): string {
  switch (kind) {
    case 'dropped-removed-field':
    case 'dropped-type-mismatch':
    case 'dropped-invalid-enum':
    case 'dropped-invalid-variant':
    case 'array-item-dropped':
      return 'drop';
    case 'reset-to-default':
    case 'added-required-default':
      return 'default';
    case 'kept':
      return 'kept';
  }
}
